import { Injectable, Inject } from '@angular/core';
import { LabTestResult, Flag } from '../lab-test-results';
import { comparators } from '../core';
import { LinkParams, dateTimeRenderer, TestLinkAgRendererComponent } from '../covance-ag-grid';
import { FilterGridColDef } from '../covance-filter';
import { LabTestResultGridRowItem } from '../on-screen-results';
import { isNil, chain, isEmpty } from 'lodash';
import { StandardTestResultHelpers } from '../on-screen-results/standard-test-results/standard-test-result-helpers';
import { SubjectTitleRenderer } from '../renderer/subject-title-renderer';
import { CovanceGridColumn } from '../covance-ag-grid/covance-grid-column';
import { SubjectColumnParams } from '../on-screen-results/standard-test-results/osr-subject-columns-renderer.component';
import { LabTestResultsLinkBuilder, LAB_TEST_RESULTS_LINK_BUILDER } from './lab-test-results-link-builder';

@Injectable()
export class LabTestsGridColumns {
    constructor(
        private helper: StandardTestResultHelpers,
        @Inject(LAB_TEST_RESULTS_LINK_BUILDER) private labTestResultsLinkBuilder: LabTestResultsLinkBuilder
    ) { }

    public getSubjectExportColumns(): CovanceGridColumn[] {
        return [
            {
                noExport: true,
                headerName: 'Subject',
                field: 'subject',
                valueGetter: (params: LabTestResultGridRowItem) => {
                    const data = params.data;
                    if (!data) {
                        return null;
                    }
                    return SubjectTitleRenderer.renderSubjectTitle(params);
                },

                comparator: (a: SubjectColumnParams, b: SubjectColumnParams) => this.helper.sortSubjects(a, b)
            },
            {
                headerName: 'Subject',
                field: 'subjectToDisplay',
                valueGetter: (params: LabTestResultGridRowItem) => {
                    const data = params.data;
                    if (!data) {
                        return null;
                    }
                    return SubjectTitleRenderer.renderTitle(params);
                }
            },
            {
                headerName: 'Subject Data Revision',
                field: 'hasSubjectDataRevision',
                valueGetter: (params: LabTestResultGridRowItem) => this.helper.getDataRevision(params, labTest => labTest.hasSubjectDataRevision)
            },
            {
                headerName: 'Screen No.',
                field: 'screeningNumber',
                cellClass: 'alwaysString'
            },
            {
                headerName: 'Age',
                field: 'age'
            },
            {
                headerName: 'Age Unit',
                field: 'ageUnit'
            },
            {
                headerName: 'Gender',
                field: 'sex'
            }
        ];
    }


    public getBaseColumns(res: LabTestResult[]): FilterGridColDef[] {
        return [
            {
                headerName: 'Test',
                field: 'test',
                pinned: 'left',
                width: 150,
                suppressSizeToFit: true,
                valueGetter: (params: LabTestResultGridRowItem) => {
                    if (!params.data) {
                        return null;
                    }
                    return this.labTestResultsLinkBuilder.getTestLink(params.data) as LinkParams;
                },
                comparator: (a: LinkParams, b: LinkParams) => comparators.stringComparator(a ? a.text : null, b ? b.text : null),
                cellRendererFramework: TestLinkAgRendererComponent
            },
            {
                headerName: 'Administrative Question',
                field: 'administrativeQuestion',
                hide: true,
                valueGetter: (params: LabTestResultGridRowItem) => this.helper.getAdminQuestionRenderer(params)
            },
            {
                headerName: 'Range',
                field: 'range',
                pinned: 'left',
                width: 80,
                valueGetter: (params: LabTestResultGridRowItem) => this.helper.getRangeRenderer(res, params)
            },
            {
                headerName: 'Units',
                field: 'units',
                pinned: 'left',
                width: 80,
                valueGetter: (params: LabTestResultGridRowItem) => this.helper.getUnits(res, params)
            },
            {
                headerName: 'Test Group',
                field: 'testGroup',
                valueGetter: (params: LabTestResultGridRowItem): string => {
                    if (!params.data) {
                        return null;
                    }

                    return params.data.testGroup;
                },
                rowGroupIndex: 0,
                hide: true
            }
        ];
    }

    public getColumnsForExport(baseColumns: FilterGridColDef[]): CovanceGridColumn[] {
        const columnsToExport: CovanceGridColumn[] = [
            ...this.getSubjectExportColumns(),
            {
                headerName: 'Visit Description',
                field: 'visitName'
            },
            {
                headerName: 'Visit Date',
                field: 'visitCollectionDate',
                valueGetter: (params: LabTestResultGridRowItem) => {
                    if (!params.data) {
                        return null;
                    }
                    return dateTimeRenderer({ value: params.data.visitCollectionDate });
                }
            },
            {
                headerName: 'Visit Data Revision',
                field: 'hasVisitDataRevision',
                valueGetter: (params: LabTestResultGridRowItem) => this.getDataRevision(params, labTest => labTest.hasVisitDataRevision)
            },
            {
                headerName: 'Accession',
                field: 'accessionNumber'
            },
            {
                headerName: 'Result',
                field: 'results',
                valueGetter: (params: LabTestResultGridRowItem) => this.getResult(params)
            },
            {
                headerName: 'Result Data Revision',
                field: 'hasResultDataRevision',
                valueGetter: (params: LabTestResultGridRowItem) => this.getDataRevision(params, labTest => labTest.hasResultDataRevision)
            },
            {
                headerName: 'Flags',
                field: 'flags',
                valueGetter: (params: LabTestResultGridRowItem) => this.getFlags(params)
            },
            {
                headerName: 'Comment',
                field: 'comments'
            },
            {
                headerName: 'Cancellation Reason',
                field: 'cancellationReason'
            }
        ];
        return [...baseColumns, ...columnsToExport];
    }

    public getFlags(params: LabTestResultGridRowItem): string {
        if (!params || !params.node || !params.data) {
            return '';
        }

        const data = params.data;

        if (params.node.group || data.isCancelled) {
            return '';
        }

        if (data.isFlagged) {
            if (isEmpty(data.flags)) {
                return '';
            }

            return this.sortFlags(data.flags);

        } else {
            return '';
        }
    }

    public sortFlags(flags: Flag[]): string {
        return chain(flags)
            .groupBy(f => f.category)
            .sortBy((flagsByCategory: Flag[]) => flagsByCategory[0].category)
            .map((flagsByCategory: Flag[]) =>
                chain(flagsByCategory)
                    .map(f => f.name)
                    .uniq()
                    .sort()
                    .value()
                    .join(', ')
            )
            .value()
            .join(' ');
    }

    private getResult(params: LabTestResultGridRowItem): string {
        if (!params || !params.node || !params.data) {
            return '';
        }

        if (params.node.group || params.data.isCancelled) {
            return '';
        }

        const tempResult = params.data.currentResult;

        return !isNil(tempResult) ? tempResult.stringResult : '';
    }

    private getDataRevision(params: LabTestResultGridRowItem, hasDataRevision: (labTest: LabTestResult) => boolean): 'R' | '' {
        if (!params || !params.node || !params.data) {
            return '';
        }

        return hasDataRevision(params.data) ? 'R' : '';
    }
}

